import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authenticatedAxios } from "../apis";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { getAssetName } from "../utils";
import CoinLogo from "../components/commons/CoinLogo";
import BotLogo from "../components/commons/BotLogo";

// ─── helpers ──────────────────────────────────────────────────────────────────
const safe = (val) =>
  val === null || val === undefined || val === "" ? "-" : val;

const formatDate = (date) => {
  if (!date) return "-";
  if (typeof date === "number") {
    return new Date(date * 1000).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return new Date(date).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── skeleton components ──────────────────────────────────────────────────────
const SkeletonBox = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const SkeletonRow = ({ cols = 6 }) => (
  <tr className="border-t border-gray-50">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <SkeletonBox className="h-4 w-full" />
      </td>
    ))}
  </tr>
);

const StatCardSkeleton = ({ className = "" }) => (
  <div
    className={`p-6 py-4 rounded-xl shadow-[0px_1px_2px_-1px_#0000001A,0px_1px_3px_0px_#0000001A] ${className}`}
  >
    <SkeletonBox className="h-2.5 w-28 mb-3" />
    <SkeletonBox className="h-7 w-36" />
  </div>
);

const BorderCardSkeleton = ({ className = "" }) => (
  <div className={`border border-gray-200 rounded-lg p-4 ${className}`}>
    <SkeletonBox className="h-2.5 w-20 mb-3" />
    <SkeletonBox className="h-6 w-24" />
  </div>
);

// ─── tab icons ────────────────────────────────────────────────────────────────
const PortfolioIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);
const BotsIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <path d="M12 3a2 2 0 1 1 0 4" />
    <path d="M12 7v4" />
    <circle cx="8" cy="16" r="1" fill="currentColor" stroke="none" />
    <circle cx="16" cy="16" r="1" fill="currentColor" stroke="none" />
  </svg>
);
const ReferralsIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const AffiliateIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

// ─── reusable table cells ─────────────────────────────────────────────────────
const Th = ({ children, right = false }) => (
  <th
    className={`px-4 py-2.5 text-[11px] font-semibold text-gray-400 tracking-wider border-b border-gray-100 bg-gray-50 ${
      right ? "text-right" : "text-left"
    }`}
  >
    {children}
  </th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-[13px] text-gray-600 ${className}`}>
    {children}
  </td>
);

// ─── limit options ────────────────────────────────────────────────────────────
const LIMIT_OPTIONS = [10, 25, 50, 100];

// ─── main component ───────────────────────────────────────────────────────────
export default function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("portfolio");

  // ── per-section state ──────────────────────────────────────────────────────
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  const [botsData, setBotsData] = useState(null);
  const [botsLoading, setBotsLoading] = useState(true);

  const [referralsData, setReferralsData] = useState(null);
  const [referralsLoading, setReferralsLoading] = useState(true);

  const [tableData, setTableData] = useState([]);
  const [tableDataLoading, setTableDataLoading] = useState(true);

  // ── bot modal state ────────────────────────────────────────────────────────
  const [selectedBot, setSelectedBot] = useState(null);
  const [botDetail, setBotDetail] = useState(null);
  const [botLoading, setBotLoading] = useState(false);
  const [botTab, setBotTab] = useState("trade_history");
  const [botPage, setBotPage] = useState(1);
  const [botPagination, setBotPagination] = useState(null);
  const [botLimit, setBotLimit] = useState(10); // ← NEW: limit state

  // ── single effect, 4 independent fetches ──────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    // 1. core user info
    (async () => {
      setUserLoading(true);
      try {
        const { data } = await authenticatedAxios.get(
          `/api/admin/users/${userId}/`,
          { signal },
        );
        setUserData(data);
      } catch (e) {
        if (e.name !== "CanceledError") console.error("user:", e);
      } finally {
        setUserLoading(false);
      }
    })();

    // 2. bots
    (async () => {
      setBotsLoading(true);
      try {
        const { data } = await authenticatedAxios.get(
          `/api/admin/users/${userId}/bots`,
          { signal },
        );
        setBotsData({
          active_count: data.active_bots ?? 0,
          inactive_count: data.inactive_bots ?? 0,
          total_profit: data.total_bot_profit ?? 0,
          bots: (data.bots ?? []).map((b) => ({
            ...b,
            status: b.is_active ? "active" : "inactive",
            profit: parseFloat(b.profit ?? 0),
          })),
        });
      } catch (e) {
        if (e.name !== "CanceledError") console.error("bots:", e);
      } finally {
        setBotsLoading(false);
      }
    })();

    // 3. referrals
    (async () => {
      setReferralsLoading(true);
      try {
        const { data } = await authenticatedAxios.get(
          `/api/admin/users/${userId}/referrals`,
          { signal },
        );
        setReferralsData({
          referral_code: data.referral_code ?? "-",
          total_referrals: data.total_referrals ?? 0,
          active_referrals: data.active_referrals ?? 0,
          total_commission: data.total_commission ?? 0,
          referral_list: (data.referrals ?? []).map((r) => ({
            uid: r.id,
            name: r.name ?? "-",
            email: r.email ?? "-",
            join_date: r.join_date ?? "-",
            status: r.is_active ? "Active" : "Inactive",
            commission: r.commission ?? 0,
          })),
        });
      } catch (e) {
        if (e.name !== "CanceledError") console.error("referrals:", e);
      } finally {
        setReferralsLoading(false);
      }
    })();

    // 4. portfolio table data
    (async () => {
      setTableDataLoading(true);
      try {
        const { data } = await authenticatedAxios.get(
          `/api/admin/users/${userId}/table-data`,
          { signal },
        );
        setTableData(Array.isArray(data) ? data : (data.table_data ?? []));
      } catch (e) {
        if (e.name !== "CanceledError") console.error("table-data:", e);
      } finally {
        setTableDataLoading(false);
      }
    })();

    return () => controller.abort();
  }, [userId]);

  const totalBalance = useMemo(
    () => tableData.reduce((sum, r) => sum + parseFloat(r.total_usdt || 0), 0),
    [tableData],
  );

  // ── fetchBotDetail now accepts a limit param ───────────────────────────────
  const fetchBotDetail = async (
    botId,
    tab = "trade_history",
    page = 1,
    limit = botLimit,
  ) => {
    setBotLoading(true);
    try {
      const { data } = await authenticatedAxios.get(
        `/api/admin/users/${userId}/bot/${botId}?tab=${tab}&page=${page}&per_page=${limit}`,
      );
      setBotDetail(data);
      setBotPagination(data.pagination || null);
      setBotPage(page);
      setBotLimit(limit);
    } catch (e) {
      console.error(e);
    } finally {
      setBotLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedBot(null);
    setBotDetail(null);
    setBotPagination(null);
    setBotPage(1);
    setBotLimit(10); // reset limit on close
  };

  const tabs = [
    { id: "portfolio", label: "Portfolio", Icon: PortfolioIcon },
    { id: "bots", label: "Trading Bots", Icon: BotsIcon },
    { id: "referrals", label: "Referrals", Icon: ReferralsIcon },
    { id: "affiliate", label: "Affiliate", Icon: AffiliateIcon },
  ];

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100 p-5">
      {/* ── PAGE HEADER ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 mb-3.5">
        <button
          onClick={() => navigate("/admin-v2/users")}
          className="inline-flex items-center gap-1.5 bg-gray-600 hover:bg-gray-700 text-white text-[13px] font-medium px-3.5 py-1.5 rounded-md transition-colors"
        >
          <ArrowLeftIcon className="w-3.5 h-3.5" />
          Back to Users
        </button>
        <div>
          <h1 className="text-[22px] font-bold text-gray-900 leading-tight">
            User Details
          </h1>
          {userLoading ? (
            <SkeletonBox className="h-3 w-48 mt-1.5" />
          ) : (
            <p className="text-[12px] text-gray-400 mt-0.5">
              {safe(userData?.email)}
            </p>
          )}
        </div>
      </div>

      {/* ── USER INFO CARD ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 mb-3.5">
        <div className="grid grid-cols-5 px-6 py-4">
          {userLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i}>
                  <SkeletonBox className="h-2.5 w-16 mb-2" />
                  <SkeletonBox className="h-4 w-28" />
                </div>
              ))
            : [
                { label: "User Name", value: safe(userData?.username) },
                { label: "Email", value: safe(userData?.email) },
                { label: "Join Date", value: formatDate(userData?.join_date) },
                {
                  label: "Last Login",
                  value: formatDate(userData?.last_login),
                },
                {
                  label: "Renewal Due",
                  value: formatDate(userData?.renewal_due),
                },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-[11px] text-gray-400 mb-1">{label}</p>
                  <p className="text-[13px] font-medium text-gray-900">
                    {value}
                  </p>
                </div>
              ))}
        </div>
      </div>

      {/* ── TABS + CONTENT ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl overflow-hidden">
        {/* tab pills */}
        <div className="flex items-center border-b border-gray-100">
          {tabs.map(({ id, label, Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm transition-all ${
                  active
                    ? "bg-blue-500 text-white border-blue-500 font-medium"
                    : "bg-[#F3F4F6] text-gray-500 border-gray-200 hover:bg-gray-50"
                }`}
              >
                <Icon />
                {label}
              </button>
            );
          })}
        </div>

        {/* ─────────────── PORTFOLIO ──────────────────────────────────── */}
        {activeTab === "portfolio" && (
          <>
            {/* stat cards */}
            <div className="grid grid-cols-4 px-6 py-5 gap-3">
              {/* Aggregate Balance — depends on tableData */}
              {tableDataLoading ? (
                <StatCardSkeleton />
              ) : (
                <div className="p-6 py-4 rounded-xl shadow-[0px_1px_2px_-1px_#0000001A,0px_1px_3px_0px_#0000001A]">
                  <p className="text-[11px] text-gray-400 mb-1.5">
                    Aggregate Account Balance
                  </p>
                  <p className="text-[20px] font-bold text-gray-900 tracking-tight">
                    {totalBalance.toFixed(2)} USDT
                  </p>
                </div>
              )}

              {/* Profit cards — depend on userData */}
              {[
                { key: "total_profit", label: "Total Profit" },
                { key: "monthly_profit", label: "Monthly Profit" },
                { key: "weekly_profit", label: "Weekly Profit" },
              ].map(({ key, label }) =>
                userLoading ? (
                  <StatCardSkeleton key={key} />
                ) : (
                  <div
                    key={key}
                    className="p-6 py-4 rounded-xl shadow-[0px_1px_2px_-1px_#0000001A,0px_1px_3px_0px_#0000001A]"
                  >
                    <p className="text-[11px] text-gray-400 mb-1.5">{label}</p>
                    <p className="text-[20px] font-bold text-gray-900 tracking-tight">
                      {safe(userData?.summary?.[key])} USDT
                    </p>
                  </div>
                ),
              )}
            </div>

            {/* portfolio table */}
            <div className="p-6 pt-0">
              <div className="border border-[#DBEAFE] overflow-hidden rounded-xl">
                {/* balance header */}
                <div className="bg-[#EFF6FF] border-b border-[#DBEAFE] px-5 py-3 text-[14px] font-semibold text-gray-900 text-center">
                  {tableDataLoading ? (
                    <SkeletonBox className="h-4 w-32 mx-auto" />
                  ) : (
                    `${totalBalance.toFixed(2)} USDT`
                  )}
                </div>

                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <Th>EXCHANGE</Th>
                      <Th>TOKEN</Th>
                      <Th>AMOUNT</Th>
                      <Th>PRICE</Th>
                      <Th>24H CHANGE</Th>
                      <Th right>TOTAL (USDT)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableDataLoading
                      ? Array.from({ length: 6 }).map((_, i) => (
                          <SkeletonRow key={i} cols={6} />
                        ))
                      : tableData.map((r, i) => {
                          const ch = parseFloat(r.change || 0);
                          return (
                            <tr
                              key={i}
                              className="border-t border-gray-50 hover:bg-gray-50 transition-colors"
                            >
                              <Td className="text-left">{safe(r.exchange)}</Td>
                              <Td>
                                <div className="flex items-center gap-2">
                                  <CoinLogo
                                    asset={getAssetName(r.token)}
                                    className="max-w-full object-contain uppercase !w-[24px] !h-[24px] rounded-full"
                                  />
                                  <span className="font-medium text-gray-800">
                                    {safe(r.token)}
                                  </span>
                                </div>
                              </Td>
                              <Td className="text-left">{safe(r.amount)}</Td>
                              <Td className="text-left">{safe(r.price)}</Td>
                              <Td
                                className={
                                  (ch >= 0
                                    ? "text-green-600"
                                    : "text-red-500") + " text-left"
                                }
                              >
                                {ch >= 0 ? "+" : ""}
                                {safe(r.change)}%
                              </Td>
                              <td className="px-4 py-3 text-[13px] text-gray-600 text-right">
                                {safe(r.total_usdt)}
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ─────────────── TRADING BOTS ───────────────────────────────── */}
        {activeTab === "bots" && (
          <div className="p-5">
            {/* stat cards */}
            <div className="grid grid-cols-3 gap-4 mb-5">
              {botsLoading ? (
                <>
                  <BorderCardSkeleton className="text-center" />
                  <BorderCardSkeleton className="text-center" />
                  <BorderCardSkeleton className="text-center" />
                </>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-[11px] text-gray-400 mb-1.5">
                      Active Bots
                    </p>
                    <p className="text-[20px] font-bold text-gray-900">
                      {botsData?.active_count}
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                    <p className="text-[11px] text-gray-400 mb-1.5">
                      Inactive Bots
                    </p>
                    <p className="text-[20px] font-bold text-gray-900">
                      {botsData?.inactive_count}
                    </p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-[11px] text-gray-400 mb-1.5">
                      Total Bot Profit
                    </p>
                    <p className="text-[20px] font-bold text-blue-600">
                      ${botsData?.total_profit}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* bots table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 text-[13px] font-semibold text-gray-900">
                List of Bots
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    {[
                      "NAME",
                      "EXCHANGE",
                      "STRATEGY",
                      "PAIR",
                      "STATUS",
                      "PROFIT",
                    ].map((h) => (
                      <Th key={h}>{h}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {botsLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <SkeletonRow key={i} cols={6} />
                      ))
                    : (botsData?.bots ?? []).map((bot, i) => (
                        <tr
                          key={i}
                          className="border-t border-gray-50 hover:bg-gray-50 transition-colors text-left"
                        >
                          <td
                            className="px-4 py-3 text-[13px] text-blue-500 cursor-pointer hover:underline text-left"
                            onClick={() => {
                              setSelectedBot(bot);
                              setBotTab("trade_history");
                              setBotLimit(10); // reset limit when opening a new bot
                              fetchBotDetail(bot.id, "trade_history", 1, 10);
                            }}
                          >
                            {safe(bot.name)}
                          </td>
                          <Td className="capitalize">{safe(bot.exchange)}</Td>
                          <Td>{safe(bot.strategy)}</Td>
                          <Td>{safe(bot.pair)}</Td>
                          <Td>{safe(bot.status)}</Td>
                          <Td>${safe(bot.profit)}</Td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ─────────────── REFERRALS ──────────────────────────────────── */}
        {activeTab === "referrals" && (
          <div className="p-5">
            {/* stat cards */}
            <div className="grid grid-cols-4 gap-4 mb-5">
              {referralsLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <BorderCardSkeleton key={i} />
                ))
              ) : (
                <>
                  <div className="border border-purple-200 rounded-lg p-4">
                    <p className="text-[11px] text-gray-400 mb-1.5">
                      Referral Code
                    </p>
                    <p className="text-[16px] font-bold text-purple-600">
                      {safe(referralsData?.referral_code)}
                    </p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-[11px] text-gray-400 mb-1.5">
                      Total Referrals
                    </p>
                    <p className="text-[20px] font-bold text-gray-900">
                      {referralsData?.total_referrals}
                    </p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-[11px] text-gray-400 mb-1.5">
                      Active Referrals
                    </p>
                    <p className="text-[20px] font-bold text-gray-900">
                      {referralsData?.active_referrals}
                    </p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-[11px] text-gray-400 mb-1.5">
                      Total Commission
                    </p>
                    <p className="text-[20px] font-bold text-gray-900">
                      ${referralsData?.total_commission}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* referrals table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 text-[13px] font-semibold text-gray-900">
                Referral List
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    {[
                      "UID",
                      "NAME",
                      "EMAIL",
                      "JOIN DATE",
                      "STATUS",
                      "COMMISSION",
                    ].map((h) => (
                      <Th key={h}>{h}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {referralsLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <SkeletonRow key={i} cols={6} />
                      ))
                    : (referralsData?.referral_list ?? []).map((r, i) => (
                        <tr
                          key={i}
                          className="text-left border-t border-gray-50 hover:bg-gray-50 transition-colors"
                        >
                          <Td>{safe(r.uid)}</Td>
                          <Td>{safe(r.name)}</Td>
                          <Td>{safe(r.email)}</Td>
                          <Td>{safe(r.join_date)}</Td>
                          <Td>{safe(r.status)}</Td>
                          <Td>${safe(r.commission)}</Td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ─────────────── AFFILIATE ──────────────────────────────────── */}
        {activeTab === "affiliate" && (
          <div className="py-16 text-center text-[14px] text-gray-400">
            No affiliate data available.
          </div>
        )}
      </div>

      {/* ── BOT DETAIL MODAL ───────────────────────────────────────────── */}
      {selectedBot && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white w-[1100px] max-w-[95vw] max-h-[92vh] overflow-y-auto rounded-xl shadow-2xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* close */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-lg leading-none p-1 transition-colors"
            >
              ✕
            </button>

            {/* title */}
            <h2 className="text-[18px] font-bold text-gray-900 mb-4">
              {safe(selectedBot.name)}
            </h2>

            {/* top summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                {
                  label: "Bot Active Since",
                  value: safe(botDetail?.active_since || "-"),
                },
                { label: "Exchange", value: safe(selectedBot.exchange) },
                { label: "Pairing", value: safe(selectedBot.pair) },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="border border-gray-200 rounded-lg px-4 py-3 text-center"
                >
                  <p className="text-[11px] text-gray-400 mb-1">{label}</p>
                  {botLoading ? (
                    <SkeletonBox className="h-4 w-24 mx-auto mt-1" />
                  ) : (
                    <p className="text-[13px] font-semibold text-gray-900 capitalize">
                      {value}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* bot settings */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              {botLoading ? (
                <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center border-b border-gray-50 pb-1.5"
                    >
                      <SkeletonBox className="h-3 w-28" />
                      <SkeletonBox className="h-3 w-20" />
                    </div>
                  ))}
                </div>
              ) : botDetail?.settings?.fields?.length > 0 ? (
                <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
                  {botDetail.settings.fields.map((f, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center border-b border-gray-50 pb-1.5 text-[13px]"
                    >
                      <span className="text-gray-500">{safe(f.label)}</span>
                      <span className="font-medium text-gray-900">
                        {safe(f.value)}{" "}
                        <span className="text-gray-900">{f.unit || ""}</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[13px] text-gray-400 py-3">
                  No settings available
                </p>
              )}
            </div>

            {/* ── Investment section ─────────────────────────────────── */}
            <div className="border border-[#dde1e6] rounded-xl !p-5 mb-4 bg-[linear-gradient(200deg,rgba(256,256,256,1)_66.84%,#8962FF_254.21%)] text-left text-[#1F2937] text-sm">
              {/* ── modal header ────────────────────────────────────── */}
              <div className="flex flex-row items-center gap-2 w-full mb-5">
                <div className="relative flex items-center shrink-0">
                  <BotLogo
                    className="w-6 h-6 rounded-full"
                    classNameLogo="!h-[unset]"
                    botName={safe(
                      selectedBot.name?.replace(/#\d+$/, "").trim(),
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <h2 className="text-[16px] font-bold text-gray-900 leading-tight">
                    {safe(selectedBot.name?.replace(/#\d+$/, "").trim())}
                  </h2>
                  <p className="text-[#999999] leading-none text-[13px] capitalize">
                    Bot active since{" "}
                    {botLoading ? (
                      <SkeletonBox className="inline-block h-3 w-24 align-middle" />
                    ) : (
                      <span className="font-medium text-gray-600">
                        {safe(
                          botDetail?.active_since ||
                            selectedBot.active_since ||
                            "-",
                        )}
                      </span>
                    )}
                    <span className="pl-2">
                      · {safe(selectedBot.pair)} · {safe(selectedBot.exchange)}
                    </span>
                  </p>
                </div>
              </div>

              <div className="border border-[#dde1e6] bg-white rounded-xl !p-4">
                <p className="font-bold text-xl mb-1">Investment</p>

                {botLoading ? (
                  <div className="space-y-3 mt-3">
                    <div className="flex gap-4">
                      <div className="w-1/2 space-y-1.5">
                        <SkeletonBox className="h-3 w-16" />
                        <SkeletonBox className="h-5 w-28" />
                      </div>
                      <div className="w-1/2 space-y-1.5">
                        <SkeletonBox className="h-3 w-20" />
                        <SkeletonBox className="h-5 w-28" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <SkeletonBox className="h-7 w-12" />
                      <SkeletonBox className="h-7 w-32" />
                      <SkeletonBox className="h-6 w-20 rounded-md" />
                    </div>
                    <div className="flex gap-4 pt-3 border-t border-[#dde1e6]">
                      <div className="w-1/2 space-y-1.5">
                        <SkeletonBox className="h-3 w-20" />
                        <SkeletonBox className="h-5 w-24" />
                        <SkeletonBox className="h-3 w-12" />
                      </div>
                      <div className="w-1/2 space-y-1.5">
                        <SkeletonBox className="h-3 w-20" />
                        <SkeletonBox className="h-5 w-24" />
                        <SkeletonBox className="h-3 w-12" />
                      </div>
                    </div>
                    <SkeletonBox className="h-14 w-full rounded-xl mt-2" />
                  </div>
                ) : botDetail?.investment ? (
                  (() => {
                    const inv = botDetail.investment;
                    const capital = parseFloat(inv.capital ?? 0);
                    const currentValue = parseFloat(inv.current_value ?? 0);
                    const roiUsdt = parseFloat(inv.roi_usdt ?? 0);
                    const roiPct = parseFloat(inv.roi_pct ?? 0);
                    const realizedPnl = parseFloat(inv.realized_pnl ?? 0);
                    const realizedPct = parseFloat(inv.realized_pnl_pct ?? 0);
                    const unrealizedPnl = parseFloat(inv.unrealized_pnl ?? 0);
                    const unrealizedPct = parseFloat(
                      inv.unrealized_pnl_pct ?? 0,
                    );
                    const winrate = inv.winrate ?? null;

                    // ── derive base currency from pair (e.g. "HBAR/IDR" → "IDR", "BTC/USDT" → "USDT")
                    const baseCurrency =
                      inv.base_currency ||
                      (selectedBot.pair?.includes("/")
                        ? selectedBot.pair.split("/")[1].toUpperCase()
                        : "USDT");

                    const fmtN = (n) =>
                      Math.abs(n).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });
                    const sign = (n) => (n > 0 ? "+" : n < 0 ? "-" : "");
                    const clr = (n) =>
                      n > 0
                        ? "text-green-500"
                        : n < 0
                          ? "text-red-500"
                          : "text-gray-500";
                    const hasWinrate = winrate !== null && winrate > 0;

                    return (
                      <div>
                        {/* Capital / Current Value */}
                        <div className="flex mt-3">
                          <div className="w-1/2 space-y-2">
                            <p className="text-sm leading-none text-[#999999]">
                              Capital
                            </p>
                            <p className="font-bold text-[18px] leading-snug text-gray-900">
                              {fmtN(capital)}{" "}
                              <span className="text-[13px] font-medium text-gray-400">
                                {baseCurrency}
                              </span>
                            </p>
                          </div>
                          <div className="w-1/2 space-y-2">
                            <p className="text-xs leading-none text-[#999999]">
                              Current Value
                            </p>
                            <p
                              className={`font-bold text-[18px] leading-snug ${clr(currentValue - capital)}`}
                            >
                              {fmtN(currentValue)}{" "}
                              <span className="text-[13px] font-medium opacity-70">
                                {baseCurrency}
                              </span>
                            </p>
                            <p
                              className={`text-sm font-medium leading-none ${clr(roiPct)}`}
                            >
                              {sign(roiPct)}
                              {Math.abs(roiPct).toFixed(2)}%
                            </p>
                          </div>
                        </div>

                        {/* ROI */}
                        <div className="flex flex-row items-center gap-2 mt-3">
                          <p className="font-semibold text-2xl my-0 text-gray-900">
                            ROI
                          </p>
                          <div className="flex flex-row items-center gap-3">
                            <p
                              className={`font-bold text-2xl my-0 ${clr(roiUsdt)}`}
                            >
                              {sign(roiUsdt)}
                              {fmtN(roiUsdt)}{" "}
                              <span className="text-[16px] font-medium opacity-80">
                                {baseCurrency}
                              </span>
                            </p>
                            <span
                              className={`px-2 py-1 text-[14px] border rounded-md whitespace-nowrap ${
                                roiPct >= 0
                                  ? "bg-green-500/10 text-green-500 border-green-500"
                                  : "bg-red-500/10 text-red-500 border-red-500"
                              }`}
                            >
                              ({sign(roiPct)}
                              {Math.abs(roiPct).toFixed(2)}%)
                            </span>
                          </div>
                        </div>

                        {/* Realized / Unrealized */}
                        <div className="flex justify-between text-sm mt-3 pt-3">
                          <div className="w-1/2 space-y-2">
                            <p className="text-sm text-[#999999] leading-none">
                              Realized P/L
                            </p>
                            <p
                              className={`font-medium text-[18px] leading-snug ${clr(realizedPnl)}`}
                            >
                              {sign(realizedPnl)}
                              {fmtN(realizedPnl)}{" "}
                              <span className="text-[13px] font-medium opacity-70">
                                {baseCurrency}
                              </span>
                            </p>
                            <p
                              className={`text-sm font-medium leading-none ${clr(realizedPct)}`}
                            >
                              {sign(realizedPct)}
                              {Math.abs(realizedPct).toFixed(2)}%
                            </p>
                          </div>
                          <div className="w-1/2 space-y-2">
                            <p className="text-sm text-[#999999] leading-none">
                              Unrealized P/L
                            </p>
                            <p
                              className={`font-medium text-[18px] leading-snug ${clr(unrealizedPnl)}`}
                            >
                              {sign(unrealizedPnl)}
                              {fmtN(unrealizedPnl)}{" "}
                              <span className="text-[13px] font-medium opacity-70">
                                {baseCurrency}
                              </span>
                            </p>
                            <p
                              className={`text-sm font-medium leading-none ${clr(unrealizedPct)}`}
                            >
                              {sign(unrealizedPct)}
                              {Math.abs(unrealizedPct).toFixed(2)}%
                            </p>
                          </div>
                        </div>

                        {/* Win Rate */}
                        <div className="mt-4 flex items-center justify-between p-3 rounded-xl border border-[#dde1e6] bg-[#f8f9fa]">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                              <svg
                                className="w-5 h-5 text-indigo-500"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-[12px] font-bold text-[#999999] uppercase tracking-wider leading-none mb-1">
                                Bot Accuracy
                              </p>
                              <p className="text-sm text-[#1F2937] font-medium leading-none">
                                Win Rate Performance
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span
                              className={`px-3 py-1 rounded-full font-bold text-lg ${
                                !hasWinrate
                                  ? "bg-gray-100 text-gray-400"
                                  : winrate >= 50
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                              }`}
                            >
                              {!hasWinrate ? "-" : `${winrate}%`}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-center text-[13px] text-gray-400 py-4">
                    No investment data available
                  </p>
                )}
              </div>
            </div>

            {/* ── bot sub-tabs + limit select ──────────────────────── */}
            <div className="flex items-center border-b border-gray-200 mb-3 !mt-5">
              {["trade_history", "bot_log"].map((t) => (
                <button
                  key={t}
                  onClick={() => {
                    setBotTab(t);
                    fetchBotDetail(selectedBot.id, t, 1, botLimit);
                  }}
                  className={`pb-2 mr-6 text-[13px] border-b-2 -mb-px transition-colors ${
                    botTab === t
                      ? "border-gray-900 text-gray-900 font-semibold"
                      : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  {t === "trade_history" ? "Trade History" : "Bot Log"}
                </button>
              ))}

              {/* ── right side: limit select + record count ── */}
              <div className="ml-auto flex items-center gap-3 mt-[-1rem]">
                {/* Limit selector */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] text-gray-400">Show</span>
                  <select
                    value={botLimit}
                    onChange={(e) => {
                      const newLimit = Number(e.target.value);
                      setBotLimit(newLimit);
                      fetchBotDetail(selectedBot.id, botTab, 1, newLimit);
                    }}
                    className="text-[12px] border border-gray-200 rounded-md px-2 py-1 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 cursor-pointer"
                  >
                    {LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span className="text-[12px] text-gray-400">rows</span>
                </div>

                {/* Record count */}
                <span className="text-[12px] text-gray-400">
                  {botPagination?.total ? `${botPagination.total} records` : ""}
                </span>
              </div>
            </div>

            {/* bot table */}
            <div className="max-h-[23rem] overflow-y-auto border border-gray-200 rounded-lg">
              {botLoading ? (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      {(botTab === "trade_history"
                        ? [
                            "PAIR",
                            "ENTRY TIME",
                            "EXIT TIME",
                            "ENTRY PRICE",
                            "CLOSE PRICE",
                            "AMOUNT",
                            "PNL",
                          ]
                        : ["ID", "COIN", "STATUS", "MESSAGE", "CREATED"]
                      ).map((h) => (
                        <Th key={h}>{h}</Th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: botLimit }).map((_, i) => (
                      <SkeletonRow
                        key={i}
                        cols={botTab === "trade_history" ? 7 : 5}
                      />
                    ))}
                  </tbody>
                </table>
              ) : !botDetail?.records?.length ? (
                <div className="py-10 text-center text-[13px] text-gray-400">
                  No data available
                </div>
              ) : botTab === "trade_history" ? (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      {[
                        "PAIR",
                        "ENTRY TIME",
                        "EXIT TIME",
                        "ENTRY PRICE",
                        "CLOSE PRICE",
                        "AMOUNT",
                        "PNL",
                      ].map((h) => (
                        <Th key={h}>{h}</Th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {botDetail.records.map((r, i) => (
                      <tr
                        key={i}
                        className="border-t border-gray-50 hover:bg-gray-50"
                      >
                        <Td>
                          {safe(r.coin_name)}/{safe(r.base_currency_name)}
                        </Td>
                        <Td>{safe(r.entry_time)}</Td>
                        <Td>{safe(r.exit_time)}</Td>
                        <Td>{safe(r.entry_price)}</Td>
                        <Td>{safe(r.close_price)}</Td>
                        <Td>{safe(r.amount)}</Td>
                        <td
                          className={`px-4 py-3 text-[13px] font-medium ${
                            parseFloat(r.pnl) >= 0
                              ? "text-green-600"
                              : "text-red-500"
                          }`}
                        >
                          {safe(r.pnl)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#F2F4F8]">
                      {["ID", "COIN", "STATUS", "MESSAGE", "CREATED"].map(
                        (h) => (
                          <Th key={h}>{h}</Th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {botDetail.records.map((r, i) => (
                      <tr
                        key={i}
                        className="border-t border-gray-50 hover:bg-gray-50"
                      >
                        <Td>{safe(r.id)}</Td>
                        <Td>{safe(r.coin)}</Td>
                        <Td>{safe(r.status)}</Td>
                        <Td>{safe(r.message)}</Td>
                        <Td>{safe(r.created_at)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* pagination */}
            {botPagination && botPagination.total_pages > 1 && (
              <div className="flex items-center justify-center mt-4 text-[13px]">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() =>
                      fetchBotDetail(
                        selectedBot.id,
                        botTab,
                        botPage - 1,
                        botLimit,
                      )
                    }
                    disabled={botPage <= 1}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md disabled:opacity-30 hover:bg-gray-100 transition-colors text-gray-600"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Previous
                  </button>

                  {(() => {
                    const total = botPagination.total_pages;
                    const current = botPage;
                    const pages = [];
                    const addPage = (p) =>
                      pages.push({ type: "page", value: p });
                    const addEllipsis = (key) =>
                      pages.push({ type: "ellipsis", value: key });

                    if (total <= 7) {
                      for (let i = 1; i <= total; i++) addPage(i);
                    } else {
                      addPage(1);
                      if (current > 3) addEllipsis("start");
                      for (
                        let i = Math.max(2, current - 1);
                        i <= Math.min(total - 1, current + 1);
                        i++
                      ) {
                        addPage(i);
                      }
                      if (current < total - 2) addEllipsis("end");
                      addPage(total);
                    }

                    return pages.map((item) =>
                      item.type === "ellipsis" ? (
                        <span
                          key={item.value}
                          className="px-2 py-1.5 text-gray-400"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={item.value}
                          onClick={() =>
                            fetchBotDetail(
                              selectedBot.id,
                              botTab,
                              item.value,
                              botLimit,
                            )
                          }
                          className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors font-medium ${
                            item.value === current
                              ? "bg-indigo-50 text-indigo-600 border border-indigo-100"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {item.value}
                        </button>
                      ),
                    );
                  })()}

                  <button
                    onClick={() =>
                      fetchBotDetail(
                        selectedBot.id,
                        botTab,
                        botPage + 1,
                        botLimit,
                      )
                    }
                    disabled={botPage >= botPagination.total_pages}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md disabled:opacity-30 hover:bg-gray-100 transition-colors text-gray-600"
                  >
                    Next
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* footer */}
            <div className="flex justify-end mt-4">
              <button className="bg-[#5E48A3] hover:bg-[#5E48CA] text-white text-[13px] font-medium px-4 py-2 rounded-lg transition-colors flex items-center">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mr-2"
                >
                  <g clipPath="url(#clip0_382_3572)">
                    <path
                      d="M3.33333 2.5H15L17.2558 4.75583C17.4121 4.91208 17.5 5.124 17.5 5.345V16.6667C17.5 16.8877 17.4122 17.0996 17.2559 17.2559C17.0996 17.4122 16.8877 17.5 16.6667 17.5H3.33333C3.11232 17.5 2.90036 17.4122 2.74408 17.2559C2.5878 17.0996 2.5 16.8877 2.5 16.6667V3.33333C2.5 3.11232 2.5878 2.90036 2.74408 2.74408C2.90036 2.5878 3.11232 2.5 3.33333 2.5V2.5ZM5.83333 3.33333V7.5H13.3333V3.33333H5.83333ZM5 10V15.8333H15V10H5ZM10.8333 4.16667H12.5V6.66667H10.8333V4.16667Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_382_3572">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
